// CampaignDetailPage — US-123, US-124, US-131, US-132, US-133, US-134,
// US-135, US-136, US-137, US-138.
//
// Renders the campaign header (back link + name + status pill + sub-row +
// status-aware action group) + body (banner if applicable, funnel,
// cadence preview, spawned conversations).

import { useEffect, useState } from 'react';

import {
  useBusinesses,
  useCampaign,
  useDeleteCampaign,
  useLaunchCampaign,
  useLinkedinAccounts,
  usePauseCampaign,
  useResumeCampaign,
  type Campaign,
  type CampaignStep,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Avatar,
  Banner,
  Button,
  CampaignStatusPill,
  CadencePreview,
  Card,
  Skeleton,
  SourcePill,
  Spinner,
  toast,
  type CadenceShape,
  type SourceKey,
} from '@barel/ui';

import { formatDate } from '@/lib/format';
import { confirm } from '@/features/shell/GlobalConfirmModal';
import { TrialExpiredOverlay } from '@/features/billing/TrialExpiredOverlay';

import { AutoPauseDisplay } from './AutoPauseDisplay';
import { CookieErrorDisplay } from './CookieErrorDisplay';
import { FunnelMetricsCard } from './FunnelMetricsCard';
import { SpawnedConvList } from './SpawnedConvList';
import { SourceProgressCard } from './SourceProgressCard';
import { buildCadenceCopy } from './lib/copy';
import { validateCampaignSteps } from './lib/sequence';

export interface CampaignDetailPageProps {
  id: string;
  onBack: () => void;
  /** Deep-link to /accounts on Reconnect (US-134). */
  onReconnect?: (accountId: string) => void;
}

function sourceTypeToSourceKey(sourceType: Campaign['sourceType']): SourceKey {
  switch (sourceType) {
    case 'salesnav':
    case 'linkedin':
      return 'Internal';
    case 'csv':
      return 'CSV';
  }
}

function stepsToCadence(steps: readonly CampaignStep[], invitHasMessage: boolean): CadenceShape {
  const map = new Map<string, CampaignStep>();
  for (const s of steps) map.set(s.stepKind, s);
  return {
    invitMsg: invitHasMessage,
    fu1Days: map.get('fu1')?.enabled ? (map.get('fu1')?.delayDays ?? 0) : 0,
    fu2Days: map.get('fu2')?.enabled ? (map.get('fu2')?.delayDays ?? 0) : 0,
    fu3Days: map.get('fu3')?.enabled ? (map.get('fu3')?.delayDays ?? 0) : 0,
  };
}

export function CampaignDetailPage({ id, onBack, onReconnect }: CampaignDetailPageProps) {
  const t = useT('campaigns');
  const detailQ = useCampaign(id);
  const accountsQ = useLinkedinAccounts();
  const businessesQ = useBusinesses();

  const launchMut = useLaunchCampaign();
  const pauseMut = usePauseCampaign();
  const resumeMut = useResumeCampaign();
  const deleteMut = useDeleteCampaign();

  const [busy, setBusy] = useState(false);

  const campaign = detailQ.data?.campaign;
  const steps = detailQ.data?.steps ?? [];
  const account = (accountsQ.data?.accounts ?? []).find(
    (a) => a.id === campaign?.linkedinAccountId,
  );
  const business = (businessesQ.data?.businesses ?? []).find((b) => b.id === campaign?.businessId);

  // Reset busy when the campaign status updates from a mutation.
  useEffect(() => {
    setBusy(false);
  }, [campaign?.status]);

  if (detailQ.isError) {
    return (
      <section className="flex flex-col gap-3" data-testid="campaign-detail-error">
        <Banner tone="error" title={t('detail.errorTitle')}>
          {t('detail.errorBody')}
        </Banner>
      </section>
    );
  }

  if (detailQ.isLoading || !campaign) {
    return (
      <section className="flex flex-col gap-3" data-testid="campaign-detail-loading">
        <Skeleton variant="rect" height={56} />
        <Skeleton variant="rect" height={120} />
        <Skeleton variant="rect" height={200} />
      </section>
    );
  }

  const cadenceCopy = buildCadenceCopy(t);
  const cadence = stepsToCadence(steps, campaign.invitHasMessage);
  const sequenceValidation = validateCampaignSteps(steps);

  const onLaunch = async () => {
    const ok = await confirm({
      title: t('launch.modalTitle', { name: campaign.name }),
      body:
        campaign.sourceType === 'csv'
          ? t('launch.modalBody', {
              account: account?.name ?? '—',
              leads: campaign.leadsCount,
              dailyLimit: campaign.dailyLimit,
            })
          : t('launch.searchModalBody', {
              account: account?.name ?? '—',
              dailyLimit: campaign.dailyLimit,
            }),
      confirmLabel: t('launch.confirm'),
      cancelLabel: t('launch.cancel'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await launchMut.mutateAsync({ id: campaign.id });
      toast.success({ title: t('toast.launched') });
    } catch (err) {
      // SPEC-CAMPAIGN-LOOP §6 — 409 `campaign.no_leads`: the cohort is
      // empty (broken CSV import / no source data). Dedicated copy so the
      // user knows to fix the source, not retry.
      const code = (err as { code?: string } | null)?.code;
      if (code === 'campaign.no_leads') {
        toast.error({ title: t('launch.noLeads') });
      } else {
        toast.error({ title: t('toast.error') });
      }
    } finally {
      setBusy(false);
    }
  };

  const onPause = async () => {
    setBusy(true);
    try {
      await pauseMut.mutateAsync({ id: campaign.id });
      toast.success({ title: t('toast.paused') });
    } catch {
      toast.error({ title: t('toast.error') });
    } finally {
      setBusy(false);
    }
  };

  const onResume = async () => {
    setBusy(true);
    try {
      await resumeMut.mutateAsync({ id: campaign.id });
      toast.success({ title: t('toast.resumed') });
    } catch {
      toast.error({ title: t('toast.error') });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: t('delete.modalTitle', { name: campaign.name }),
      body: t('delete.modalBody'),
      confirmLabel: t('delete.confirm'),
      cancelLabel: t('delete.cancel'),
      tone: 'destructive',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMut.mutateAsync({ id: campaign.id });
      toast.success({ title: t('toast.deleted') });
      onBack();
    } catch {
      toast.error({ title: t('toast.error') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-5" data-testid="campaign-detail">
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="campaign-back">
          {t('detail.back')}
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-3 pb-4 border-b border-default">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-primary text-xl font-bold m-0" data-testid="campaign-name">
                {campaign.name}
              </h1>
              <CampaignStatusPill status={campaign.status} size="sm">
                {t(`status.${campaign.status}`)}
              </CampaignStatusPill>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-tertiary text-xs">
              {account ? (
                <span className="inline-flex items-center gap-1.5">
                  <Avatar
                    size="xs"
                    tone="identity"
                    identityColor={account.color}
                    initials={account.initials}
                    alt={account.name}
                  />
                  {account.name}
                </span>
              ) : null}
              <span aria-hidden="true">·</span>
              <span>{business?.name ?? '—'}</span>
              <span aria-hidden="true">·</span>
              <SourcePill source={sourceTypeToSourceKey(campaign.sourceType)} size="sm">
                {t(`source.${campaign.sourceType}`)}
              </SourcePill>
              {campaign.startedAt ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    {t('detail.startedAt', {
                      date: formatDate(campaign.startedAt),
                    })}
                  </span>
                </>
              ) : null}
            </div>
            {campaign.description ? (
              <p className="text-secondary text-sm mt-2 max-w-prose leading-relaxed">
                {campaign.description}
              </p>
            ) : null}
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {busy ? <Spinner size="sm" /> : null}
            {campaign.status === 'draft' ? (
              <TrialExpiredOverlay>
                <Button
                  variant="primary"
                  size="md"
                  disabled={busy || !sequenceValidation.valid}
                  onClick={() => void onLaunch()}
                  data-testid="campaign-launch"
                >
                  {t('detail.headerLaunch')}
                </Button>
              </TrialExpiredOverlay>
            ) : null}
            {campaign.status === 'active' ? (
              <Button
                variant="ghost"
                size="md"
                disabled={busy}
                onClick={() => void onPause()}
                data-testid="campaign-pause"
              >
                {t('detail.headerPause')}
              </Button>
            ) : null}
            {campaign.status === 'paused' || campaign.status === 'paused_auto' ? (
              <TrialExpiredOverlay>
                <Button
                  variant="primary"
                  size="md"
                  disabled={busy}
                  onClick={() => void onResume()}
                  data-testid="campaign-resume"
                >
                  {t('detail.headerResume')}
                </Button>
              </TrialExpiredOverlay>
            ) : null}
            {campaign.status === 'error' ? (
              <Button
                variant="danger"
                size="md"
                onClick={() => onReconnect?.(campaign.linkedinAccountId)}
                data-testid="campaign-reconnect"
              >
                {t('detail.headerReconnect')}
              </Button>
            ) : null}
            {campaign.status !== 'finished' ? (
              <Button
                variant="danger"
                size="md"
                disabled={busy}
                onClick={() => void onDelete()}
                data-testid="campaign-delete"
              >
                {t('detail.headerDelete')}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Banners */}
        {campaign.status === 'error' && campaign.errorReason ? (
          <CookieErrorDisplay reason={campaign.errorReason} />
        ) : null}
        {campaign.status === 'paused_auto' && campaign.pauseReason ? (
          <AutoPauseDisplay reason={campaign.pauseReason} />
        ) : null}
        {campaign.status === 'draft' && !sequenceValidation.valid ? (
          <Banner
            tone="warning"
            title={t('launch.invalidSequenceTitle')}
            data-testid="campaign-sequence-invalid"
          >
            {t('launch.invalidSequenceBody')}
          </Banner>
        ) : null}
      </div>

      {/* Body */}
      {campaign.sourceType !== 'csv' ? <SourceProgressCard campaign={campaign} /> : null}

      <FunnelMetricsCard campaign={campaign} />

      <Card padding="lg" radius="2xl" data-testid="cadence-section">
        <div className="text-tertiary text-xs font-bold uppercase mb-3">
          {t('detail.cadenceTitle')}
        </div>
        <CadencePreview cadence={cadence} dailyLimit={campaign.dailyLimit} copy={cadenceCopy} />
      </Card>

      <SpawnedConvList campaignId={campaign.id} />
    </section>
  );
}
